import { useState, useMemo } from 'react';
import { useTabWithHash } from '../../hooks/useTabWithHash';

// ── Seuils légaux CSE — Art. L2311-2 CT ──────────────────────────────────────
const SEUIL_CSE        = 11;
const SEUIL_CSE_ELARGI = 50;
const DUREE_MANDAT_ANS = 4;
// Limite des 3 mandats successifs supprimée — Loi n°2025-989 du 24 octobre 2025 (Art. L2314-33 CT modifié)

// ── Rôles du CSE ─────────────────────────────────────────────────────────────
const ROLES_CSE = [
  { key:'president',  label:'Président (employeur)',  icon:'👔' },
  { key:'secretaire', label:'Secrétaire',             icon:'📋' },
  { key:'tresorier',  label:'Trésorier',              icon:'💰' },
  { key:'titulaire',  label:'Membre titulaire',       icon:'✅' },
  { key:'suppleant',  label:'Membre suppléant',       icon:'🔄' },
  { key:'ref_harcel', label:'Référent harcèlement',   icon:'🛡️' },
  { key:'rss',        label:'Représentant syndical',  icon:'🤝' },
];

// ── Domaines d'attributions CSE ───────────────────────────────────────────────
const ATTRIBUTIONS_CSE = [
  { seuil:11,  label:'Présentation des réclamations des salariés',         base:'Art. L2312-5 CT' },
  { seuil:11,  label:'Droit d\'alerte en cas d\'atteinte aux droits',       base:'Art. L2312-59 CT' },
  { seuil:11,  label:'Consultation sur les licenciements économiques',      base:'Art. L2312-6 CT' },
  { seuil:50,  label:'Consultations périodiques obligatoires (3/an)',       base:'Art. L2312-17 CT' },
  { seuil:50,  label:'Base de données économiques et sociales (BDES)',      base:'Art. L2312-21 CT' },
  { seuil:50,  label:'Activités sociales et culturelles (ASC)',             base:'Art. L2312-78 CT' },
  { seuil:50,  label:'CSSCT (commission santé, sécurité, conditions)',      base:'Art. L2315-36 CT' },
  { seuil:50,  label:'Réunions mensuelles obligatoires',                   base:'Art. L2315-27 CT' },
  { seuil:50,  label:'Accord de performance collective',                   base:'Art. L2254-2 CT' },
  { seuil:300, label:'Commissions formation, égalité, logement',           base:'Art. L2315-45 CT' },
];

// ── Calcul ETP légal — Art. L1111-2 CT ───────────────────────────────────────
// CDI temps plein = 1 ETP
// CDI temps partiel = heures contractuelles / durée légale hebdo (35h)
// CDD / intérim / saisonnier = (nb semaines travaillées dans l'année) / 52
function calcEffectifETP(staff = []) {
  const actifs = staff.filter(s => s.statut === 'Actif');
  let etp = 0;
  let nbPhysiques = 0;

  for (const s of actifs) {
    const typeContrat = (s.typeContrat || s.contrat || '').toUpperCase();
    const isCDD = typeContrat.includes('CDD') || typeContrat.includes('INTERIM') || typeContrat.includes('SAISONNIER');
    const tempsPartiel = s.tempsPartiel || s.tempsTravail === 'Partiel' || false;
    // Quotité horaire : si le salarié a un champ heures/semaine, sinon 35h
    const heuresHebdo = parseFloat(s.heuresHebdo || s.quotite || (tempsPartiel ? 17.5 : 35)) || 35;

    nbPhysiques++;

    if (isCDD) {
      // Nb semaines travaillées dans l'année (approx via dates contrat si dispo)
      const debut  = s.dateEntree  ? new Date(s.dateEntree)  : null;
      const finCtx = s.dateSortie  ? new Date(s.dateSortie)  : new Date();
      const semaines = debut
        ? Math.min(52, Math.max(1, Math.ceil((finCtx - debut) / (7 * 86400000))))
        : 26; // par défaut 26 semaines si dates inconnues
      etp += (semaines / 52) * (heuresHebdo / 35);
    } else {
      // CDI
      etp += heuresHebdo / 35;
    }
  }

  return { nbPhysiques, etp: Math.round(etp * 100) / 100 };
}

// ── Calendrier type d'élections CSE ──────────────────────────────────────────
function calcCalendrier(dateDebut) {
  if (!dateDebut) return [];
  const d = new Date(dateDebut + 'T12:00:00');
  return [
    { j:-60, label:'Information des salariés + invitation organisations syndicales (J-60)', obligatoire:true },
    { j:-45, label:'Protocole d\'accord préélectoral (PAP) signé (J-45)',                   obligatoire:true },
    { j:-30, label:'Affichage listes électorales (J-30)',                                   obligatoire:true },
    { j:-15, label:'Dépôt des listes de candidats (J-15)',                                  obligatoire:true },
    { j:-5,  label:'Affichage listes définitives (J-5)',                                    obligatoire:true },
    { j:0,   label:'🗳️ Date du scrutin (1er tour)',                                         obligatoire:true },
    { j:14,  label:'2ème tour si nécessaire (J+14)',                                        obligatoire:false },
  ].map(e => {
    const date = new Date(d);
    date.setDate(date.getDate() + e.j);
    return { ...e, date: date.toLocaleDateString('fr-FR') };
  });
}

// ── Génération PV PDF via window.print ───────────────────────────────────────
function imprimerPV(reunion, infosAsso) {
  const association = infosAsso?.nom || 'CMOB Assens';
  const dateStr = reunion.date
    ? new Date(reunion.date + 'T12:00:00').toLocaleDateString('fr-FR', { weekday:'long', day:'numeric', month:'long', year:'numeric' })
    : '—';
  const heures = reunion.heureDebut || reunion.heureFin
    ? `de ${reunion.heureDebut || '—'} à ${reunion.heureFin || '—'}`
    : '';

  const html = `<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8"/>
  <title>PV CSE — ${dateStr}</title>
  <style>
    @page { size: A4; margin: 2.5cm 2cm; }
    body { font-family: 'Times New Roman', serif; font-size: 11pt; color: #1a1a1a; line-height: 1.6; }
    h1 { font-size: 14pt; text-align: center; margin-bottom: 4px; }
    h2 { font-size: 11pt; margin-top: 18px; border-bottom: 1px solid #555; padding-bottom: 3px; }
    .entete { text-align: center; margin-bottom: 24px; }
    .meta { font-size: 10pt; color: #444; margin-bottom: 6px; }
    .contenu { white-space: pre-wrap; margin-top: 12px; min-height: 200px; }
    .pied { margin-top: 60px; display: flex; justify-content: space-between; font-size: 10pt; }
    .signature { border-top: 1px solid #555; width: 200px; text-align: center; padding-top: 4px; }
    @media print { button { display: none; } }
  </style>
</head>
<body>
  <div class="entete">
    <div style="font-size:10pt; color:#555; margin-bottom:4px;">${association}</div>
    <h1>PROCÈS-VERBAL DE RÉUNION</h1>
    <div style="font-size:11pt; font-weight:bold;">Comité Social et Économique (CSE)</div>
    <div class="meta" style="margin-top:8px;">
      Réunion du <strong>${dateStr}</strong>${heures ? ' &mdash; ' + heures : ''}
    </div>
    <div class="meta">Objet : <strong>${reunion.objet || '—'}</strong></div>
  </div>

  <h2>Personnes présentes</h2>
  <div class="contenu">${(reunion.pvPresents || '').trim() || '[À compléter]'}</div>

  <h2>Ordre du jour</h2>
  <div class="contenu">${(reunion.pvOrdreJour || '').trim() || '[À compléter]'}</div>

  <h2>Délibérations et décisions</h2>
  <div class="contenu">${(reunion.pvContenu || '').trim() || '[À compléter]'}</div>

  <h2>Questions diverses</h2>
  <div class="contenu">${(reunion.pvQuestions || '').trim() || 'Néant'}</div>

  <div class="pied">
    <div class="signature">Le Secrétaire du CSE</div>
    <div class="signature">Le Président du CSE</div>
  </div>
</body>
</html>`;

  const win = window.open('', '_blank', 'width=900,height=700');
  win.document.write(html);
  win.document.close();
  win.onload = () => win.print();
}

const TABS = ['tableau-bord', 'membres', 'elections', 'reunions', 'attributions'];

// ── Formulaire réunion vide ───────────────────────────────────────────────────
const FORM_REUNION_VIDE = {
  date:'', heureDebut:'', heureFin:'', objet:'', statut:'Planifiée',
  pvPresents:'', pvOrdreJour:'', pvContenu:'', pvQuestions:'', pvRealise:false,
};

export function CSE({ staff, infosAsso, isAdmin, isRH }) {
  const [tab, setTab] = useTabWithHash(TABS, 'tableau-bord');

  const [membres,   setMembres]   = useState([]);
  const [reunions,  setReunions]  = useState([]);
  const [elections, setElections] = useState({ dateElection:'', dateFin:'', protocole:false, nbSieges:3 });
  const [showForm,  setShowForm]  = useState(false);
  const [formM,     setFormM]     = useState({ prenom:'', nom:'', role:'titulaire', email:'', dateMandat:'', section:'' });

  // ── Réunions ──
  const [showFormReunion, setShowFormReunion] = useState(false);
  const [formR,           setFormR]           = useState(FORM_REUNION_VIDE);
  const [pvOpenId,        setPvOpenId]        = useState(null); // index de la réunion dont le PV est ouvert en édition
  const [pvDraft,         setPvDraft]         = useState({}); // brouillon PV en cours d'édition

  // ── Effectif légal Art. L1111-2 CT ──
  const { nbPhysiques, etp } = useMemo(() => calcEffectifETP(staff), [staff]);
  // L'effectif légal pour les seuils CSE est l'ETP arrondi à l'entier inférieur
  const effectifLegal = Math.floor(etp);
  const cseOblig      = effectifLegal >= SEUIL_CSE;
  const cseElargi     = effectifLegal >= SEUIL_CSE_ELARGI;

  const membresActifs = membres.filter(m => {
    if (!m.dateMandat || !elections.dateElection) return true;
    const fin = new Date(elections.dateElection);
    fin.setFullYear(fin.getFullYear() + DUREE_MANDAT_ANS);
    return new Date() <= fin;
  });

  const calendrier = calcCalendrier(elections.dateElection);

  const finMandat = elections.dateElection
    ? (() => { const d = new Date(elections.dateElection + 'T12:00:00'); d.setFullYear(d.getFullYear() + DUREE_MANDAT_ANS); return d; })()
    : null;
  const joursFinMandat = finMandat ? Math.ceil((finMandat - new Date()) / 86400000) : null;

  // ── Helpers réunions ──
  const ajouterReunion = () => {
    if (!formR.date || !formR.objet.trim()) return;
    setReunions(prev => [...prev, { ...formR, pvRealise: false }]);
    setFormR(FORM_REUNION_VIDE);
    setShowFormReunion(false);
  };

  const supprimerReunion = (idx) => {
    if (!window.confirm('Supprimer cette réunion ?')) return;
    setReunions(prev => prev.filter((_, i) => i !== idx));
    if (pvOpenId === idx) setPvOpenId(null);
  };

  const ouvrirEditionPV = (idx) => {
    const r = reunions[idx];
    setPvDraft({
      pvPresents:   r.pvPresents   || '',
      pvOrdreJour:  r.pvOrdreJour  || '',
      pvContenu:    r.pvContenu    || '',
      pvQuestions:  r.pvQuestions  || '',
    });
    setPvOpenId(idx);
  };

  const sauvegarderPV = (idx) => {
    setReunions(prev => prev.map((r, i) => i === idx
      ? { ...r, ...pvDraft, pvRealise: true }
      : r
    ));
    setPvOpenId(null);
  };

  const supprimerPV = (idx) => {
    if (!window.confirm('Supprimer le PV de cette réunion ?')) return;
    setReunions(prev => prev.map((r, i) => i === idx
      ? { ...r, pvPresents:'', pvOrdreJour:'', pvContenu:'', pvQuestions:'', pvRealise:false }
      : r
    ));
    if (pvOpenId === idx) setPvOpenId(null);
  };

  const reunionsTri = [...reunions].reverse(); // du plus récent au plus ancien

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">🗳️ Comité Social et Économique (CSE)</h1>
          <p className="page-subtitle">Gestion des IRP — Art. L2311-1 et suivants CT</p>
        </div>
      </div>

      {/* ── Bannière statut CSE ── */}
      <div style={{ marginBottom: 16, padding: '14px 18px', borderRadius: 10,
        background: cseOblig ? (cseElargi ? 'rgba(185,28,28,0.07)' : 'rgba(0,48,135,0.06)') : 'rgba(107,114,128,0.06)',
        border: `1px solid ${cseOblig ? (cseElargi ? 'rgba(185,28,28,0.2)' : 'rgba(0,48,135,0.15)') : 'rgba(107,114,128,0.2)'}`,
        display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
        <div style={{ fontSize: '2rem' }}>{cseOblig ? (cseElargi ? '⚠️' : 'ℹ️') : '✅'}</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 800, fontSize: '0.95rem' }}>
            {!cseOblig
              ? `CSE non obligatoire — effectif légal : ${effectifLegal} ETP (${nbPhysiques} sal. physiques) — seuil : ${SEUIL_CSE}`
              : cseElargi
                ? `CSE obligatoire avec attributions élargies — ${effectifLegal} ETP (${nbPhysiques} physiques) ≥ ${SEUIL_CSE_ELARGI}`
                : `CSE obligatoire — ${effectifLegal} ETP (${nbPhysiques} physiques) · ${SEUIL_CSE} ≤ effectif < ${SEUIL_CSE_ELARGI}`}
          </div>
          <div style={{ fontSize: '0.78rem', color: 'var(--muted)', marginTop: 3 }}>
            {!cseOblig
              ? `Le CSE devient obligatoire si l'effectif ETP atteint ${SEUIL_CSE} pendant 12 mois consécutifs (Art. L2311-2 CT)`
              : `Effectif en ETP — Art. L1111-2 CT · Durée du mandat : ${DUREE_MANDAT_ANS} ans (Art. L2314-33 CT)`}
          </div>
        </div>
        {finMandat && (
          <div style={{ textAlign: 'right', flexShrink: 0 }}>
            <div style={{ fontSize: '0.72rem', color: 'var(--muted)' }}>Fin de mandat</div>
            <div style={{ fontWeight: 800, fontSize: '0.9rem',
              color: joursFinMandat < 90 ? 'var(--danger)' : joursFinMandat < 180 ? 'var(--warning)' : 'var(--success)' }}>
              {finMandat.toLocaleDateString('fr-FR')}
              {joursFinMandat !== null && (
                <span style={{ marginLeft: 6, fontSize: '0.75rem' }}>
                  ({joursFinMandat > 0 ? `dans ${joursFinMandat}j` : 'EXPIRÉ'})
                </span>
              )}
            </div>
            {joursFinMandat < 90 && (
              <div style={{ fontSize: '0.7rem', color: 'var(--danger)', fontWeight: 600 }}>
                ⚠️ Renouvellement à organiser dès maintenant
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Onglets ── */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        {[
          { id:'tableau-bord', label:'📊 Tableau de bord' },
          { id:'membres',      label:`👥 Membres (${membresActifs.length})` },
          { id:'elections',    label:'🗳️ Élections' },
          { id:'reunions',     label:`📋 Réunions (${reunions.length})` },
          { id:'attributions', label:'⚖️ Attributions légales' },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            style={{ padding: '7px 16px', borderRadius: 20, fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer',
              background: tab === t.id ? 'var(--bleu)' : 'transparent',
              color: tab === t.id ? '#fff' : 'var(--muted)',
              border: `2px solid ${tab === t.id ? 'var(--bleu)' : 'var(--border)'}` }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ══ TABLEAU DE BORD ══ */}
      {tab === 'tableau-bord' && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 12, marginBottom: 20 }}>
            {[
              { label: 'Salariés physiques', val: nbPhysiques,            color: '#003087' },
              { label: 'ETP légal (Art. L1111-2)', val: `${etp}`,         color: '#0369a1' },
              { label: 'Membres CSE',         val: membresActifs.length,  color: '#7c3aed' },
              { label: 'Réunions tenues',     val: reunions.filter(r=>r.statut==='Tenue').length, color: '#065f46' },
              { label: 'Sièges prévus',       val: elections.nbSieges || '—', color: '#b45309' },
            ].map(s => (
              <div key={s.label} className="card" style={{ padding: '14px 16px', borderLeft: `3px solid ${s.color}` }}>
                <div style={{ fontSize: '1.5rem', fontWeight: 800, color: s.color }}>{s.val}</div>
                <div style={{ fontSize: '0.75rem', color: 'var(--muted)' }}>{s.label}</div>
              </div>
            ))}
          </div>

          {joursFinMandat !== null && joursFinMandat < 90 && (
            <div className="card" style={{ borderLeft: '4px solid var(--danger)', padding: '14px 18px', marginBottom: 12 }}>
              <div style={{ fontWeight: 700, color: 'var(--danger)' }}>
                🚨 Mandat expirant dans {joursFinMandat}j — Organiser les élections
              </div>
              <div style={{ fontSize: '0.78rem', color: 'var(--muted)', marginTop: 4 }}>
                L'invitation des organisations syndicales doit être envoyée au moins 60 jours avant (Art. L2314-4 CT).
              </div>
            </div>
          )}

          <div className="card" style={{ padding: '14px 18px' }}>
            <div style={{ fontWeight: 700, marginBottom: 10 }}>📋 Rappels réglementaires</div>
            {[
              { ok: elections.dateElection,                             label: 'Date des dernières élections renseignée',   base: 'Art. L2314-33 CT' },
              { ok: membresActifs.length >= 1,                          label: 'Au moins un membre CSE actif',              base: 'Art. L2311-2 CT' },
              { ok: membresActifs.some(m=>m.role==='ref_harcel'),        label: 'Référent harcèlement désigné',              base: 'Art. L1153-5-1 CT' },
              { ok: membresActifs.some(m=>m.role==='secretaire'),        label: 'Secrétaire désigné',                        base: 'Art. L2315-23 CT' },
              { ok: membresActifs.some(m=>m.role==='tresorier'),         label: 'Trésorier désigné (si ≥ 50 sal.)', base: 'Art. L2315-24 CT', condSeuil: 50 },
              { ok: reunions.length > 0,                                 label: `Réunions tenues (${cseElargi?'mensuelle':'min. 6/an'})`, base: cseElargi?'Art. L2315-27 CT':'Art. L2315-28 CT' },
            ].filter(r => !r.condSeuil || effectifLegal >= r.condSeuil).map((r, i, arr) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0',
                borderBottom: i < arr.length - 1 ? '1px solid var(--border)' : 'none' }}>
                <span style={{ fontSize: '1rem' }}>{r.ok ? '✅' : '❌'}</span>
                <span style={{ flex: 1, fontSize: '0.84rem', color: r.ok ? 'var(--text)' : 'var(--danger)', fontWeight: r.ok ? 400 : 600 }}>{r.label}</span>
                <span style={{ fontSize: '0.72rem', color: 'var(--muted)', flexShrink: 0 }}>{r.base}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ══ MEMBRES ══ */}
      {tab === 'membres' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
            {(isAdmin || isRH) && (
              <button className="btn btn-primary" onClick={() => { setFormM({ prenom:'', nom:'', role:'titulaire', email:'', dateMandat:'', section:'' }); setShowForm(true); }}>
                + Ajouter un membre
              </button>
            )}
          </div>

          {membres.length === 0 ? (
            <div className="card" style={{ padding: 40, textAlign: 'center', color: 'var(--muted)' }}>
              <div style={{ fontSize: '2rem', marginBottom: 8 }}>👥</div>
              Aucun membre CSE enregistré. Ajoutez les membres après les élections.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {membres.map((m, i) => {
                const role = ROLES_CSE.find(r => r.key === m.role);
                return (
                  <div key={i} className="card" style={{ padding: '12px 18px', display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ fontSize: '1.4rem' }}>{role?.icon || '👤'}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700 }}>{m.prenom} {m.nom}</div>
                      <div style={{ fontSize: '0.78rem', color: 'var(--muted)' }}>
                        {role?.label} · {m.section || 'Toutes sections'}
                        {m.dateMandat && ` · Mandat depuis le ${new Date(m.dateMandat).toLocaleDateString('fr-FR')}`}
                      </div>
                    </div>
                    {m.email && (
                      <div style={{ fontSize: '0.75rem', color: 'var(--muted)' }}>{m.email}</div>
                    )}
                    {(isAdmin || isRH) && (
                      <button className="btn btn-danger btn-sm" onClick={() => setMembres(prev => prev.filter((_, j) => j !== i))}>🗑</button>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {showForm && (
            <div className="card" style={{ padding: '16px 18px', marginTop: 16 }}>
              <div className="form-grid">
                <div className="form-group"><label>Prénom</label><input value={formM.prenom} onChange={e=>setFormM(f=>({...f,prenom:e.target.value}))}/></div>
                <div className="form-group"><label>Nom</label><input value={formM.nom} onChange={e=>setFormM(f=>({...f,nom:e.target.value}))}/></div>
                <div className="form-group">
                  <label>Rôle</label>
                  <select value={formM.role} onChange={e=>setFormM(f=>({...f,role:e.target.value}))}>
                    {ROLES_CSE.map(r=><option key={r.key} value={r.key}>{r.icon} {r.label}</option>)}
                  </select>
                </div>
                <div className="form-group"><label>Email</label><input value={formM.email} onChange={e=>setFormM(f=>({...f,email:e.target.value}))}/></div>
                <div className="form-group"><label>Date de début de mandat</label><input type="date" value={formM.dateMandat} onChange={e=>setFormM(f=>({...f,dateMandat:e.target.value}))}/></div>
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
                <button className="btn btn-secondary" onClick={() => setShowForm(false)}>Annuler</button>
                <button className="btn btn-primary" disabled={!formM.prenom || !formM.nom} onClick={() => { setMembres(prev => [...prev, formM]); setShowForm(false); }}>
                  💾 Ajouter
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ══ ÉLECTIONS ══ */}
      {tab === 'elections' && (
        <div>
          <div className="card" style={{ padding: '16px 18px', marginBottom: 16 }}>
            <div style={{ fontWeight: 700, marginBottom: 12 }}>⚙️ Paramètres des élections</div>
            <div className="form-grid">
              <div className="form-group">
                <label>Date du scrutin (1er tour)</label>
                <input type="date" value={elections.dateElection} onChange={e=>setElections(p=>({...p,dateElection:e.target.value}))}/>
              </div>
              <div className="form-group">
                <label>Nombre de sièges</label>
                <select value={elections.nbSieges} onChange={e=>setElections(p=>({...p,nbSieges:parseInt(e.target.value)}))}>
                  {[1,2,3,4,5,6,7,8,9,10].map(n=><option key={n}>{n}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label style={{ display:'flex',alignItems:'center',gap:8,cursor:'pointer' }}>
                  <input type="checkbox" checked={elections.protocole} onChange={e=>setElections(p=>({...p,protocole:e.target.checked}))} style={{width:16,height:16}}/>
                  Protocole préélectoral signé
                </label>
              </div>
            </div>
          </div>

          {elections.dateElection && (
            <div className="card" style={{ padding: '16px 18px' }}>
              <div style={{ fontWeight: 700, marginBottom: 12 }}>📅 Calendrier légal des élections</div>
              <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginBottom: 12 }}>
                Art. L2314-4 CT — Délais obligatoires à respecter sous peine de nullité des élections
              </div>
              {calendrier.map((e, i) => {
                const isPast = new Date(e.date.split('/').reverse().join('-')) < new Date();
                return (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0',
                    borderBottom: i < calendrier.length - 1 ? '1px solid var(--border)' : 'none' }}>
                    <span style={{ width: 24, height: 24, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '0.75rem', fontWeight: 700, flexShrink: 0,
                      background: isPast ? 'rgba(22,163,74,0.1)' : 'rgba(0,48,135,0.1)',
                      color: isPast ? '#16a34a' : 'var(--bleu)' }}>
                      {isPast ? '✓' : i + 1}
                    </span>
                    <div style={{ flex: 1, fontSize: '0.82rem' }}>{e.label}</div>
                    <div style={{ fontSize: '0.78rem', fontWeight: 600, flexShrink: 0,
                      color: isPast ? 'var(--muted)' : 'var(--bleu)' }}>{e.date}</div>
                    {e.obligatoire && <span style={{ fontSize: '0.68rem', background: 'rgba(185,28,28,0.1)', color: 'var(--danger)', padding: '1px 6px', borderRadius: 4, fontWeight: 700 }}>Obligatoire</span>}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ══ RÉUNIONS ══ */}
      {tab === 'reunions' && (
        <div>
          {/* En-tête */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div style={{ fontSize: '0.82rem', color: 'var(--muted)' }}>
              {cseElargi ? 'Réunion mensuelle obligatoire (Art. L2315-27 CT)' : 'Minimum 6 réunions/an (Art. L2315-28 CT)'}
            </div>
            {(isAdmin || isRH) && (
              <button className="btn btn-primary btn-sm" onClick={() => { setFormR(FORM_REUNION_VIDE); setShowFormReunion(true); }}>
                + Ajouter une réunion
              </button>
            )}
          </div>

          {/* Formulaire d'ajout */}
          {showFormReunion && (
            <div className="card" style={{ padding: '16px 18px', marginBottom: 16, border: '2px solid var(--bleu)' }}>
              <div style={{ fontWeight: 700, marginBottom: 12 }}>📋 Nouvelle réunion CSE</div>
              <div className="form-grid">
                <div className="form-group">
                  <label>Date *</label>
                  <input type="date" value={formR.date} onChange={e=>setFormR(f=>({...f,date:e.target.value}))}/>
                </div>
                <div className="form-group">
                  <label>Heure de début</label>
                  <input type="time" value={formR.heureDebut} onChange={e=>setFormR(f=>({...f,heureDebut:e.target.value}))}/>
                </div>
                <div className="form-group">
                  <label>Heure de fin</label>
                  <input type="time" value={formR.heureFin} onChange={e=>setFormR(f=>({...f,heureFin:e.target.value}))}/>
                </div>
                <div className="form-group">
                  <label>Statut</label>
                  <select value={formR.statut} onChange={e=>setFormR(f=>({...f,statut:e.target.value}))}>
                    <option>Planifiée</option>
                    <option>Tenue</option>
                    <option>Annulée</option>
                  </select>
                </div>
              </div>
              <div className="form-group" style={{ marginTop: 8 }}>
                <label>Objet / ordre du jour *</label>
                <input value={formR.objet} onChange={e=>setFormR(f=>({...f,objet:e.target.value}))} placeholder="Ex : Consultation sur l'organisation du travail..."/>
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
                <button className="btn btn-secondary" onClick={() => setShowFormReunion(false)}>Annuler</button>
                <button className="btn btn-primary" disabled={!formR.date || !formR.objet.trim()} onClick={ajouterReunion}>
                  💾 Enregistrer
                </button>
              </div>
            </div>
          )}

          {/* Liste des réunions */}
          {reunions.length === 0 ? (
            <div className="card" style={{ padding: 40, textAlign: 'center', color: 'var(--muted)' }}>
              <div style={{ fontSize: '2rem', marginBottom: 8 }}>📋</div>
              Aucune réunion CSE enregistrée.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {reunionsTri.map((r, iRev) => {
                const idx = reunions.length - 1 - iRev; // index réel dans reunions[]
                const dateStr = r.date
                  ? new Date(r.date + 'T12:00:00').toLocaleDateString('fr-FR', { weekday:'long', day:'numeric', month:'long', year:'numeric' })
                  : '—';
                const heuresStr = r.heureDebut || r.heureFin
                  ? `${r.heureDebut || '—'} → ${r.heureFin || '—'}`
                  : null;
                const statutColor = r.statut === 'Tenue' ? 'badge-green' : r.statut === 'Annulée' ? 'badge-red' : 'badge-yellow';
                const pvOuvert = pvOpenId === idx;

                return (
                  <div key={idx} className="card" style={{ padding: 0, overflow: 'hidden' }}>
                    {/* Ligne principale */}
                    <div style={{ padding: '12px 18px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                      <div style={{ flex: 1, minWidth: 200 }}>
                        <div style={{ fontWeight: 700, fontSize: '0.92rem' }}>{r.objet}</div>
                        <div style={{ fontSize: '0.78rem', color: 'var(--muted)', marginTop: 2 }}>
                          {dateStr}
                          {heuresStr && <span style={{ marginLeft: 8, fontWeight: 600 }}>⏱ {heuresStr}</span>}
                        </div>
                      </div>
                      <span className={`badge ${statutColor}`}>{r.statut}</span>
                      <span className={`badge ${r.pvRealise ? 'badge-green' : 'badge-yellow'}`}>
                        {r.pvRealise ? '📄 PV rédigé' : '📄 PV à rédiger'}
                      </span>

                      {(isAdmin || isRH) && (
                        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                          {/* Bouton PV : ouvrir éditeur */}
                          <button
                            className="btn btn-secondary btn-sm"
                            onClick={() => pvOuvert ? setPvOpenId(null) : ouvrirEditionPV(idx)}
                            title={pvOuvert ? 'Fermer le PV' : r.pvRealise ? 'Modifier le PV' : 'Rédiger le PV'}
                          >
                            {pvOuvert ? '🔼 Fermer PV' : r.pvRealise ? '✏️ Modifier PV' : '📝 Rédiger PV'}
                          </button>
                          {/* Export PDF (si PV rédigé) */}
                          {r.pvRealise && (
                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={() => imprimerPV(r, infosAsso)}
                              title="Imprimer / exporter le PV en PDF"
                            >
                              🖨️ PDF
                            </button>
                          )}
                          {/* Supprimer PV */}
                          {r.pvRealise && (
                            <button
                              className="btn btn-danger btn-sm"
                              onClick={() => supprimerPV(idx)}
                              title="Supprimer le PV"
                            >
                              🗑 PV
                            </button>
                          )}
                          {/* Supprimer réunion */}
                          <button
                            className="btn btn-danger btn-sm"
                            onClick={() => supprimerReunion(idx)}
                            title="Supprimer cette réunion"
                          >
                            🗑
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Éditeur de PV inline */}
                    {pvOuvert && (
                      <div style={{ borderTop: '1px solid var(--border)', padding: '16px 18px', background: 'rgba(0,48,135,0.02)' }}>
                        <div style={{ fontWeight: 700, fontSize: '0.88rem', marginBottom: 12, color: 'var(--bleu)' }}>
                          📄 Procès-verbal — {dateStr}
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                          <div className="form-group">
                            <label>Personnes présentes</label>
                            <textarea
                              rows={4}
                              style={{ resize: 'vertical', fontFamily: 'inherit', fontSize: '0.83rem' }}
                              value={pvDraft.pvPresents}
                              onChange={e=>setPvDraft(d=>({...d,pvPresents:e.target.value}))}
                              placeholder="Prénom NOM – Rôle&#10;Prénom NOM – Rôle"
                            />
                          </div>
                          <div className="form-group">
                            <label>Ordre du jour</label>
                            <textarea
                              rows={4}
                              style={{ resize: 'vertical', fontFamily: 'inherit', fontSize: '0.83rem' }}
                              value={pvDraft.pvOrdreJour}
                              onChange={e=>setPvDraft(d=>({...d,pvOrdreJour:e.target.value}))}
                              placeholder="1. Point sur …&#10;2. Consultation sur …"
                            />
                          </div>
                        </div>
                        <div className="form-group">
                          <label>Délibérations et décisions</label>
                          <textarea
                            rows={7}
                            style={{ resize: 'vertical', fontFamily: 'inherit', fontSize: '0.83rem', width: '100%' }}
                            value={pvDraft.pvContenu}
                            onChange={e=>setPvDraft(d=>({...d,pvContenu:e.target.value}))}
                            placeholder="Résumé des échanges, votes, décisions prises…"
                          />
                        </div>
                        <div className="form-group">
                          <label>Questions diverses</label>
                          <textarea
                            rows={3}
                            style={{ resize: 'vertical', fontFamily: 'inherit', fontSize: '0.83rem', width: '100%' }}
                            value={pvDraft.pvQuestions}
                            onChange={e=>setPvDraft(d=>({...d,pvQuestions:e.target.value}))}
                            placeholder="Néant, ou liste des points abordés en fin de séance"
                          />
                        </div>
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
                          <button className="btn btn-secondary" onClick={() => setPvOpenId(null)}>Annuler</button>
                          <button className="btn btn-primary" onClick={() => sauvegarderPV(idx)}>
                            💾 Sauvegarder le PV
                          </button>
                          {pvDraft.pvContenu && (
                            <button
                              className="btn btn-secondary"
                              onClick={() => imprimerPV({ ...reunions[idx], ...pvDraft }, infosAsso)}
                            >
                              🖨️ Aperçu PDF
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ══ ATTRIBUTIONS ══ */}
      {tab === 'attributions' && (
        <div>
          <div style={{ marginBottom: 12, padding: '10px 14px', borderRadius: 8, fontSize: '0.82rem',
            background: 'rgba(0,48,135,0.05)', border: '1px solid rgba(0,48,135,0.12)', color: 'var(--bleu)' }}>
            ℹ️ Les attributions affichées correspondent à votre effectif légal de <strong>{effectifLegal} ETP</strong> ({nbPhysiques} salarié(s) physiques — Art. L1111-2 CT).
          </div>
          {ATTRIBUTIONS_CSE.filter(a => effectifLegal >= a.seuil).map((a, i) => (
            <div key={i} className="card" style={{ padding: '12px 18px', marginBottom: 8,
              borderLeft: `3px solid ${a.seuil >= 50 ? '#7c3aed' : '#003087'}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span>✅</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: '0.88rem' }}>{a.label}</div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--muted)', marginTop: 2 }}>{a.base}</div>
                </div>
                <span style={{ fontSize: '0.7rem', padding: '2px 8px', borderRadius: 10, fontWeight: 700,
                  background: a.seuil >= 50 ? 'rgba(124,58,237,0.1)' : 'rgba(0,48,135,0.08)',
                  color: a.seuil >= 50 ? '#7c3aed' : 'var(--bleu)' }}>
                  ≥ {a.seuil} ETP
                </span>
              </div>
            </div>
          ))}
          {ATTRIBUTIONS_CSE.filter(a => effectifLegal < a.seuil).length > 0 && (
            <div style={{ marginTop: 16 }}>
              <div style={{ fontWeight: 700, fontSize: '0.82rem', color: 'var(--muted)', marginBottom: 8 }}>
                🔒 Attributions non applicables à votre effectif actuel
              </div>
              {ATTRIBUTIONS_CSE.filter(a => effectifLegal < a.seuil).map((a, i) => (
                <div key={i} style={{ padding: '10px 14px', marginBottom: 6, borderRadius: 8,
                  background: 'rgba(107,114,128,0.05)', border: '1px solid rgba(107,114,128,0.15)',
                  display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ color: 'var(--muted)' }}>🔒</span>
                  <div style={{ flex: 1, fontSize: '0.82rem', color: 'var(--muted)' }}>{a.label}</div>
                  <span style={{ fontSize: '0.7rem', color: 'var(--muted)', flexShrink: 0 }}>
                    Seuil : {a.seuil} ETP · {a.base}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
